"use client";

import { useCallback } from "react";
import { resolveMessagesEntry, resolveNewConversation } from "@/lib/targeting/launcher-target";
import { slotThreadKey } from "@/lib/targeting/slot-thread";
import type { WidgetRoute } from "@/lib/widget-route";
import type { LauncherMessage } from "./use-launcher-message";

interface ThreadRef {
  chatId: string;
  threadKey: string | null;
}

/** A launcher-message quick reply waiting for its slot thread to mount and send it. */
export interface PendingSlotReply {
  threadKey: string;
  text: string;
}

/**
 * What a tap on the launcher or on its message does (owner rulings 2026-10-26):
 *  - re-entering Messages (tab or the launcher with Messages as the default tab) restores the visitor's
 *    last position there; the rules below apply to the FIRST entry;
 *  - the regular launcher opens the operator's default tab as before; the first entry into Messages
 *    (launcher with Messages as default tab, or Home -> Messages) opens the visitor's LAST ACTIVE thread,
 *    never a new one and never the page's slot thread; with no thread yet the list opens and its empty
 *    state offers the page's slot;
 *  - a click on the bubble body or a quick reply opens the shown slot's thread (the reply sends its
 *    label from there); both read the variant;
 *  - X reads it and does nothing else: no thread, no route, no panel.
 */
export function useLauncherActions(opts: {
  message: LauncherMessage;
  /** The slot rules apply (a MESSAGE slot exists and the messenger is on). */
  slotLauncher: boolean;
  /** The operator's default tab is Messages: the launcher lands in a thread instead of Home etc. */
  launcherToMessages: boolean;
  /** The visitor's threads, most recently active first. */
  threads: readonly ThreadRef[];
  isOpen: boolean;
  /** The first `visitorThreads` fetch is still in flight: land on the resume skeleton, the shell resolves it. */
  threadsLoading?: boolean;
  setRoute: (route: WidgetRoute) => void;
  /** The route was chosen here, so the "resume the latest thread" landing must not override it. */
  markResumed: () => void;
  openPanel: () => void;
  togglePanel: () => void;
  setPendingReply: (reply: PendingSlotReply) => void;
  /** Where the visitor last was inside Messages in this panel session (`null` = nothing to restore). */
  resumeRoute?: () => WidgetRoute | null;
  /** The thread key of a slot: `slot-<id>` for a local slot (the BE does not know it), else `message:<id>[:<variant>]`. */
  keyFor?: (slotId: string, variantId: string) => string;
}) {
  const { message, slotLauncher, launcherToMessages, threads, isOpen } = opts;
  const { setRoute, markResumed, openPanel, togglePanel, setPendingReply } = opts;
  const keyFor = opts.keyFor ?? ((slotId: string) => slotThreadKey(slotId));

  const openSlotThread = useCallback(
    (slotId: string, variantId: string) => {
      const key = keyFor(slotId, variantId);
      const existing = threads.find((t) => t.chatId && t.threadKey === key);
      markResumed();
      setRoute({ kind: "messages-thread", threadKey: key, chatId: existing?.chatId });
      return key;
    },
    [threads, markResumed, setRoute, keyFor],
  );

  /** The Messages landing: restore the last position, else the last active thread, else the list. */
  const { resumeRoute, threadsLoading } = opts;
  const messagesEntryRoute = useCallback((): WidgetRoute => {
    // Re-entering Messages restores the last position (list / same thread) before any resolver.
    const resumed = resumeRoute?.();
    if (resumed) {
      markResumed();
      return resumed;
    }
    // Threads not known yet: the shell's resume effect picks the last active thread (or the list).
    if (threadsLoading) return { kind: "messages-thread", pendingLatest: true };
    markResumed();
    const entry = resolveMessagesEntry(threads);
    return entry.kind === "thread"
      ? { kind: "messages-thread", chatId: entry.chatId, threadKey: entry.threadKey }
      : { kind: "messages" };
  }, [threads, threadsLoading, markResumed, resumeRoute]);

  const onLauncherToggle = useCallback(() => {
    if (!isOpen && slotLauncher && launcherToMessages) setRoute(messagesEntryRoute());
    togglePanel();
  }, [isOpen, slotLauncher, launcherToMessages, messagesEntryRoute, setRoute, togglePanel]);

  /** The route of a conversation the visitor starts manually: it begins with the page's slot text. */
  const newConversationRoute = useCallback(
    (newKey: string): WidgetRoute => {
      const plain: WidgetRoute = { kind: "messages-thread", threadKey: newKey };
      if (!slotLauncher) return plain;
      const next = resolveNewConversation(
        message.top ? { slotId: message.top.slotId, variantId: message.top.variant.id } : null,
        threads,
        keyFor,
      );
      if (next.kind === "slot-thread")
        return { kind: "messages-thread", threadKey: next.threadKey };
      if (next.kind === "topic-with-slot-text") return { ...plain, welcomeSlotId: next.slotId };
      return plain;
    },
    [slotLauncher, message.top, threads, keyFor],
  );

  const onBubbleOpen = useCallback(() => {
    const shown = message.shown;
    if (!shown) return;
    message.markRead("click");
    openSlotThread(shown.slotId, shown.variant.id);
    openPanel();
  }, [message, openSlotThread, openPanel]);

  const onBubbleReply = useCallback(
    (text: string) => {
      const shown = message.shown;
      if (!shown) return;
      message.markRead("click");
      const threadKey = openSlotThread(shown.slotId, shown.variant.id);
      setPendingReply({ threadKey, text });
      openPanel();
    },
    [message, openSlotThread, setPendingReply, openPanel],
  );

  const onBubbleDismiss = useCallback(() => message.markRead("dismiss"), [message]);

  return {
    onLauncherToggle,
    messagesEntryRoute,
    newConversationRoute,
    onBubbleOpen,
    onBubbleReply,
    onBubbleDismiss,
  };
}
